// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Meta } from '../types';
import { glossaryTerm } from '../glossary';
import { formatNumber } from '../utils/format';

/** About modal: what this is, where the data comes from, and what it cannot tell you. */
export function createAbout(meta: Meta): { open: () => void; close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'About Safeguard Emitters');

  const n = meta.national;
  const mt = (t: number) => `${(t / 1e6).toFixed(1)} Mt`;
  modal.innerHTML = `
    <button class="modal-close" aria-label="Close">✕</button>
    <h2>About Safeguard Emitters</h2>
    <p>
      This is every industrial facility covered by Australia’s ${glossaryTerm('safeguard', 'Safeguard Mechanism')} in the
      ${meta.reportingYear} reporting year — <strong>${formatNumber(n.facilities)}</strong> of the country’s largest
      emitters, each of which released more than 100,000 tonnes of ${glossaryTerm('co2e', 'CO₂-equivalent')}. Together they
      account for <strong>${mt(n.totalCovered)}</strong> of ${glossaryTerm('covered', 'covered emissions')} — roughly a
      quarter of Australia’s entire greenhouse footprint.
    </p>
    <p>
      Each facility has a legally-binding ${glossaryTerm('baseline', 'baseline')} that declines every year on a path to net
      zero by 2050. The one question this site is built to answer is simple: did a facility emit
      <strong>above or below</strong> that line? This year <strong>${n.nOver}</strong> of them came in over it, even as the
      cap tightens.
    </p>

    <h3>Where the data comes from</h3>
    <ul>
      <li>
        <strong>Clean Energy Regulator — Safeguard baselines and emissions data</strong>, the ${meta.reportingYear} and
        ${meta.prevYear} "baselines and emissions" tables. This is the official public record every covered facility is
        required to report under the National Greenhouse and Energy Reporting scheme.
      </li>
      <li><strong>ABS ASGS 2021</strong> — state and territory boundaries for the map.</li>
    </ul>

    <h3>How often it updates</h3>
    <p>
      An automated pipeline rebuilds the site once a year, matching the regulator’s annual publication cycle (the previous
      financial year’s data is released around April). Figures shown are for the ${meta.reportingYear} reporting year,
      which ended 30 June ${meta.reportingYear.slice(-2) === '25' ? '2025' : `20${meta.reportingYear.slice(-2)}`}.
    </p>

    <h3>Important limits — please read</h3>
    <ul>
      <li>
        <strong>Covered emissions are ${glossaryTerm('scope1', 'scope 1 only')}.</strong> They count what a facility
        releases on site — not the emissions from later burning the coal, gas or fuel it produces. A coal mine’s covered
        emissions are a small fraction of the emissions from the coal it digs up.
      </li>
      <li>
        <strong>"Over the line" means covered emissions above baseline.</strong> A facility can be over the line and still
        be compliant if it surrenders ${glossaryTerm('accu', 'carbon credits')} to cover the gap. The
        ${glossaryTerm('netposition', 'net position')} and Carbon Market views show that settlement; not every facility
        settles annually, so some show no net position.
      </li>
      <li>
        <strong>Emissions track production, not just efficiency.</strong> A big year-on-year jump usually means a facility
        produced more (more ore, gas or clinker), not that it suddenly ran dirtier. Baselines are partly production-adjusted
        for this reason.
      </li>
      <li>
        This is only the largest ~200 emitters. Smaller industrial sites, transport, agriculture and household emissions
        are outside the Safeguard Mechanism and not shown here.
      </li>
    </ul>

    <h3>Licence</h3>
    <p>
      Clean Energy Regulator and ABS data are published under CC BY 4.0. This is an independent project and is not
      affiliated with the regulator or any company shown.
    </p>
  `;

  document.body.append(overlay, modal);
  const close = () => {
    overlay.classList.remove('open');
    modal.classList.remove('open');
  };
  const open = () => {
    overlay.classList.add('open');
    modal.classList.add('open');
    (modal.querySelector('.modal-close') as HTMLElement)?.focus();
  };
  overlay.addEventListener('click', close);
  modal.querySelector('.modal-close')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) close();
  });
  return { open, close };
}
